import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { message } from 'antd';
import basicRingtone from '../../../resource/audio/basicRingtone.wav';
import {
  ringtonePlayErrorSelector,
  selectedRingtoneDeviceSelector,
  silentModeSelector,
} from '../store/selectors';
import { phoneQueryKeys } from '../queries/queryKeys';
import { playRingtone, stopRingtone } from '../utils/ringtoneControls';
import { setRingtonePlayError } from '../store/actions';
import useSipStates from '../../../hooks/useSipStates';

const useRingtoneElement = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { isIdle, isConnected } = useSipStates();

  const [ringtoneEl, setRingtoneEl] = useState(null);

  const selectedRingtoneDevice = useSelector(selectedRingtoneDeviceSelector);
  const silentMode = useSelector(silentModeSelector);
  const ringtonePlayError = useSelector(ringtonePlayErrorSelector);

  const calls = queryClient.getQueryData(phoneQueryKeys.calls());
  const activeCalls = calls?.activeCalls;

  const ringtonePlayErrorHandler = useCallback(
    (err) => {
      dispatch(setRingtonePlayError(err));
    },
    [dispatch]
  );

  useEffect(() => {
    let audio;
    if (isConnected) {
      audio = new Audio(basicRingtone);
      audio.loop = true;
      setRingtoneEl(audio);
    }

    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        setRingtoneEl(null);
      }
    };
  }, [isConnected]);

  // Устанавливаем устройство для воспроизведения рингтона
  useEffect(() => {
    if (typeof ringtoneEl?.setSinkId === 'function') {
      ringtoneEl
        .setSinkId(selectedRingtoneDevice.deviceId || 'default')
        .catch((err) => {
          message.error('Не удалось вывести рингтон на выбранное устройство');
          console.log('setSinkIdError', err);
        });
    }
  }, [ringtoneEl, selectedRingtoneDevice]);

  // Воспроизведение рингтона при входящих звонках и его остановка
  useEffect(() => {
    if (ringtoneEl) {
      const needPlay = Boolean(activeCalls?.length) && !silentMode && isIdle;
      needPlay
        ? playRingtone(ringtoneEl, ringtonePlayErrorHandler, ringtonePlayError)
        : stopRingtone(ringtoneEl);
    }
  }, [
    ringtoneEl,
    activeCalls,
    silentMode,
    isIdle,
    ringtonePlayError,
    ringtonePlayErrorHandler,
  ]);
};

export default useRingtoneElement;
